import React, { useEffect, useRef } from 'react';
import { Search, ShieldCheck, Handshake, MapPin, Headphones } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const ServicesSection = () => {
  const { t } = useLanguage();
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    { icon: Search, ...t.services.service1 },
    { icon: ShieldCheck, ...t.services.service2 },
    { icon: Handshake, ...t.services.service3 },
    { icon: MapPin, ...t.services.service4 },
    { icon: Headphones, ...t.services.service5 },
  ];

  return (
    <section 
      id="services" 
      ref={sectionRef}
      className="py-32 md:py-40 bg-black relative"
      data-testid="services-section"
    >
      <div className="absolute inset-0 dot-pattern opacity-20" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        {/* Section Header */}
        <div className="text-center mb-20 md:mb-24 animate-on-scroll">
          <p className="text-[11px] font-medium tracking-[0.25em] uppercase text-zinc-500 mb-4">
            {t.services.overline}
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal tracking-tight text-white">
            {t.services.title}
          </h2>
          <div className="w-16 h-px bg-white/20 mx-auto mt-8" />
        </div>

        {/* Services - Horizontal scroll on mobile, grid on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
          {services.map((service, index) => (
            <div 
              key={index}
              className="animate-on-scroll"
              style={{ transitionDelay: `${index * 0.08}s` }}
            >
              <div className="card-premium p-8 min-h-[200px] group text-center">
                {/* Icon */}
                <div className="w-12 h-12 mx-auto flex items-center justify-center border border-white/10 mb-6 group-hover:border-white/30 transition-colors duration-500">
                  <service.icon className="w-5 h-5 text-white/50 group-hover:text-white transition-colors duration-500" />
                </div>

                {/* Content */}
                <h3 className="font-sans text-xs tracking-[0.1em] uppercase font-medium text-white mb-3">
                  {service.title}
                </h3>
                <p className="text-zinc-500 text-xs leading-relaxed font-light">
                  {service.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
